import React, { useEffect, useState } from "react";
import { API_URL } from "../../imports";
import axios from "axios";
import { FaRegCopy } from "react-icons/fa";

const processCodeMap = {
    "Transcription": "TRANSCRIPT",
    "Get Ideas": "GETIDEAS",
    "Raw": "RAW",
    "Refined": "REFINED",
};

const GenerateToken = () => {
    const [formData, setFormData] = useState({
        userEmail: "",
        projectName: "",
        process: "Transcription",
    });

    const [userEmails, setUserEmails] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [allProjects, setAllProjects] = useState([]);
    const [filteredProjects, setFilteredProjects] = useState([]);
    const [token, setToken] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [copySuccess, setCopySuccess] = useState(false);

    const processOptions = ["Transcription", "Get Ideas", "Raw", "Refined"];

    useEffect(() => {
        fetchUserEmails();
        fetchProjects();
    }, []);

    const fetchUserEmails = async () => {
        try {
            const response = await axios.get(`${API_URL}/users`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("access_token")}`,
                },
            });
            setAllUsers(response.data.data || []);
            const emails = response.data.data.map(user => user.email);
            setUserEmails(emails);
        } catch (error) {
            console.error("Failed to fetch users:", error);
        }
    };

    const fetchProjects = async () => {
        try {
            const response = await axios.get(`${API_URL}/projectusers`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("access_token")}`,
                },
            });
            setAllProjects(response.data.data || []);
        } catch (error) {
            console.error("Failed to fetch projects:", error);
            setAllProjects([]);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        if (name === "userEmail") {
            if (value) {
                const selectedUser = allUsers.find(user => user.email === value);
                if (selectedUser) {
                    const userId = selectedUser.user_id;
                    const associatedProjects = allProjects.filter(
                        project => project.user_id === userId
                    );
                    setFilteredProjects(associatedProjects);
                } else {
                    setFilteredProjects([]);
                }
            } else {
                setFilteredProjects([]);
            }
            setFormData(prev => ({ ...prev, projectName: "" }));
        }
    };

    const handleGenerate = async () => {
        if (!formData.userEmail || !formData.projectName) {
            setErrorMessage("Please select an email and project name.");
            return;
        }
        try {
            const response = await axios.post(
                `${API_URL}/auth/generate-token`,
                {
                    process_code: processCodeMap[formData.process],
                    project: formData.projectName,
                },
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("access_token")}`,
                    },
                }
            );
            setToken(response.data.token);
            setErrorMessage("");
        } catch (error) {
            const errorMsg = error.response?.data?.message || "Failed to generate auth token.";
            setErrorMessage(errorMsg);
        }
    };

    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(token);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (err) {
            console.error("Failed to copy: ", err);
        }
    };

    const handleSendToken = async () => {
        try {
            await axios.post(
                `${API_URL}/auth/send-token`,
                {
                    email: formData.userEmail,
                    token: token,
                },
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("access_token")}`,
                    },
                }
            );
            alert("Token sent to email!");
        } catch (error) {
            const errorMsg = error.response?.data?.message || "Failed to send token to email.";
            setErrorMessage(errorMsg);
        }
    };

    const handleCancel = () => {
        setFormData({
            userEmail: "",
            projectName: "",
            process: "Transcription",
        });
        setToken("");
        setFilteredProjects([]);
        setErrorMessage("");
        setCopySuccess(false);
    };

    const handleBack = () => {
        setToken("");
        setErrorMessage("");
        setCopySuccess(false);
    };

    return (
        <div className="max-w-lg mx-auto bg-white rounded-lg shadow-md">
            <h3 className="font-medium leading-6 px-4 pt-4 pb-4 bg-gray-50 rounded-t-lg font-GoogleSans border-b">
                Auth Token Generator
            </h3>
            <div className="space-y-4 p-6">
                {/* Conditional Rendering for Input Forms */}
                {!token && (
                    <>
                        {/* Email Dropdown */}
                        <div>
                            <label className="text-sm text-gray-600">
                                User Email <span className="text-red-600">*</span>
                            </label>
                            <select
                                name="userEmail"
                                value={formData.userEmail}
                                onChange={handleChange}
                                className="block w-full rounded-md border-0 py-2 text-gray-900 shadow-sm ring-1 ring-gray-300 focus:ring-gray-300 sm:text-sm"
                                required
                            >
                                <option value="">Select an email</option>
                                {userEmails.map((email) => (
                                    <option key={email} value={email}>
                                        {email}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="text-sm text-gray-600">
                                Project Name <span className="text-red-600">*</span>
                            </label>
                            <select
                                name="projectName"
                                value={formData.projectName}
                                onChange={handleChange}
                                className="block w-full rounded-md border-0 py-2 text-gray-900 shadow-sm ring-1 ring-gray-300 focus:ring-gray-300 sm:text-sm"
                                disabled={!formData.userEmail}
                                required
                            >
                                <option value="">
                                    {formData.userEmail
                                        ? (filteredProjects.length > 0 ? "Select a project" : "No projects assigned")
                                        : "Please select a user first"}
                                </option>
                                {filteredProjects.map((project) => (
                                    <option key={project.id} value={project.project_name}>
                                        {project.project_name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Process Dropdown */}
                        <div>
                            <label className="text-sm text-gray-600">Process</label>
                            <select
                                name="process"
                                value={formData.process}
                                onChange={handleChange}
                                className="block w-full rounded-md border-0 py-2 text-gray-900 shadow-sm ring-1 ring-gray-300 focus:ring-gray-300 sm:text-sm"
                            >
                                {processOptions.map((option) => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Error Message */}
                        {errorMessage && (
                            <div className="text-red-600">{errorMessage}</div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex justify-end space-x-2">
                            <button
                                onClick={handleCancel}
                                className="inline-flex justify-center rounded-lg px-4 py-2 text-sm font-medium bg-red-100 text-red-900 hover:bg-red-200"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleGenerate}
                                className="inline-flex justify-center rounded-lg px-5 py-2 text-sm font-medium bg-green-100 text-green-900 hover:bg-green-200"
                            >
                                Generate Token
                            </button>
                        </div>
                    </>
                )}

                {/* Token Display Section */}
                {token && (
                    <div className="mt-6 space-y-4">
                        <label className="block text-sm text-gray-600 text-center">
                            Generated Token
                        </label>
                        <div className="flex items-center justify-center space-x-2">
                            <input
                                type="password"
                                value={token}
                                readOnly
                                className="block w-full rounded-md border-0 py-2 text-gray-900 shadow-sm ring-1 ring-gray-300 focus:ring-gray-300 sm:text-sm"
                            />
                            <button
                                onClick={copyToClipboard}
                                className="inline-flex justify-center rounded-lg px-5 py-2 text-sm font-medium bg-blue-100 text-blue-900 hover:bg-blue-200"
                                title="Copy Token"
                            >
                                <FaRegCopy className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="flex justify-between space-x-2">
                            <button
                                onClick={handleBack}
                                className="inline-flex justify-center rounded-lg px-5 py-2 text-sm font-medium bg-gray-100 text-gray-900 hover:bg-gray-200"
                            >
                                Back
                            </button>
                            <button
                                onClick={handleSendToken}
                                className="inline-flex justify-center rounded-lg px-5 py-2 text-sm font-medium bg-green-100 text-green-900 hover:bg-green-200"
                            >
                                Send Token
                            </button>
                        </div>
                        {copySuccess && (
                            <div className="text-green-600 text-sm text-center">Copied to clipboard!</div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default GenerateToken;
